import { useCallback, useEffect, useRef, useState } from "react";
import { useChatSocket } from "../context/ChatSocketContext";
import { getLoggedUser } from "../util/SecurityUtils";

const TYPING_EXPIRY_MS = 5000;
const THROTTLE_MS = 3000;

interface TypingEvent {
  userId: number;
  name: string;
}

interface TypingEntry {
  name: string;
  expiresAt: number;
}

interface UseTypingIndicatorReturn {
  typingNames: string[];
  notifyTyping: () => void;
}

const typingTopic = (tournamentId?: number | null) =>
  tournamentId != null ? `/topic/chat/tournament/${tournamentId}/typing` : "/topic/chat/global/typing";

export const useTypingIndicator = (tournamentId?: number | null): UseTypingIndicatorReturn => {
  const { subscribe, publish } = useChatSocket();
  const [entries, setEntries] = useState<Map<number, TypingEntry>>(new Map());
  const lastSentRef = useRef<number>(0);
  const currentUserId = getLoggedUser()?.id ?? -1;

  useEffect(() => {
    const unsubscribe = subscribe(typingTopic(tournamentId), (raw) => {
      const event = raw as TypingEvent;
      if (event.userId === currentUserId) return;
      setEntries((prev) => {
        const next = new Map(prev);
        next.set(event.userId, { name: event.name, expiresAt: Date.now() + TYPING_EXPIRY_MS });
        return next;
      });
    });
    return unsubscribe;
  }, [subscribe, tournamentId, currentUserId]);

  useEffect(() => {
    // Sweep expired entries every second.
    const interval = window.setInterval(() => {
      setEntries((prev) => {
        const now = Date.now();
        let changed = false;
        const next = new Map(prev);
        for (const [uid, entry] of prev) {
          if (entry.expiresAt <= now) {
            next.delete(uid);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, 1000);
    return () => window.clearInterval(interval);
  }, []);

  const notifyTyping = useCallback(() => {
    const now = Date.now();
    if (now - lastSentRef.current < THROTTLE_MS) return;
    lastSentRef.current = now;
    publish("/app/chat/typing", { tournamentId: tournamentId ?? null });
  }, [publish, tournamentId]);

  const typingNames = Array.from(entries.values())
    .filter((e) => e.expiresAt > Date.now())
    .map((e) => e.name);

  return { typingNames, notifyTyping };
};
